import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import { where } from "firebase/firestore";
import { PRODUCTS } from "../../config";
import { quotaTotals } from "../../lib/licenses";
import { distributorKeyCountQuery } from "../../lib/queries";
import { useCounts } from "../../hooks/usePaged";
import {
  EmptyState,
  Loading,
  PageHeader,
  QuotaMeter,
} from "../../components/ui";
import DistributorDialog from "./DistributorDialog";
import { useAdminData } from "./AdminData";

// Quota totals come from the distributor documents themselves.
export function useDistributorStats() {
  const { distributors } = useAdminData();
  return useMemo(
    () => distributors.map((d) => ({ ...d, totals: quotaTotals(d) })),
    [distributors],
  );
}

// Activated keys per distributor, counted by Firestore (both products).
function useActivatedCounts(distributorIds) {
  const defs = {};
  distributorIds.forEach((id) =>
    PRODUCTS.forEach((p) => {
      defs[`${id}|${p.id}`] = () =>
        distributorKeyCountQuery(p.id, id, [where("status", "==", "used")]);
    }),
  );
  const { counts } = useCounts(
    distributorIds.length ? `activated|${distributorIds.join()}` : null,
    defs,
  );
  return (id) =>
    PRODUCTS.every((p) => counts[`${id}|${p.id}`] !== undefined)
      ? PRODUCTS.reduce((sum, p) => sum + counts[`${id}|${p.id}`], 0)
      : "…";
}

const ActiveChip = ({ active }) =>
  active === false ? <Chip size="small" label="Paused" /> : null;

export default function DistributorsPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();
  const { distributorsLoading } = useAdminData();
  const stats = useDistributorStats();
  const activated = useActivatedCounts(stats.map((d) => d.id));
  const [search, setSearch] = useState("");
  const [creating, setCreating] = useState(false);

  const term = search.trim().toLowerCase();
  const rows = stats.filter(
    (d) =>
      !term ||
      [d.name, d.region, d.contactName, d.contactEmail].some((v) =>
        (v || "").toLowerCase().includes(term),
      ),
  );

  const open = (d) => navigate(`/distributors/${d.id}`);

  return (
    <>
      <PageHeader
        eyebrow="Partners"
        title="Distributors"
        subtitle="Regional partners, their key quotas and who can sign in for them."
        actions={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreating(true)}
          >
            Add distributor
          </Button>
        }
      />

      {distributorsLoading ? (
        <Loading />
      ) : stats.length === 0 ? (
        <EmptyState
          title="No distributors yet"
          body="Add a distributor, give them a quota (e.g. 50 LiFE Joy Annual keys) and a login. They then issue keys to their customers one at a time — they never see a list of unused keys."
          action={
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setCreating(true)}
            >
              Add distributor
            </Button>
          }
        />
      ) : (
        <>
          <TextField
            placeholder="Search distributors…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ mb: 2, width: { xs: "100%", sm: 320 } }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              },
            }}
          />
          {isMobile ? (
            rows.map((d) => (
              <Card key={d.id} sx={{ mb: 1.25 }}>
                <CardActionArea onClick={() => open(d)}>
                  <CardContent>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        gap: 1,
                      }}
                    >
                      <Typography variant="h6">{d.name}</Typography>
                      <ActiveChip active={d.active} />
                    </Box>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      gutterBottom
                    >
                      {d.region}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      <strong>{d.totals.remaining}</strong> keys left ·{" "}
                      {activated(d.id)} activated
                    </Typography>
                    <QuotaMeter
                      allocated={d.totals.allocated}
                      issued={d.totals.issued}
                    />
                  </CardContent>
                </CardActionArea>
              </Card>
            ))
          ) : (
            <Paper variant="outlined">
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Distributor</TableCell>
                      <TableCell>Region</TableCell>
                      <TableCell sx={{ width: 220 }}>Quota used</TableCell>
                      <TableCell align="right">Left</TableCell>
                      <TableCell align="right">Activated</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((d) => (
                      <TableRow
                        key={d.id}
                        hover
                        onClick={() => open(d)}
                        sx={{ cursor: "pointer" }}
                      >
                        <TableCell>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Typography variant="body2" fontWeight={600}>
                              {d.name}
                            </Typography>
                            <ActiveChip active={d.active} />
                          </Box>
                          <Typography variant="caption" color="text.secondary">
                            {[d.contactName, d.contactEmail]
                              .filter(Boolean)
                              .join(" · ")}
                          </Typography>
                        </TableCell>
                        <TableCell>{d.region}</TableCell>
                        <TableCell>
                          <QuotaMeter
                            allocated={d.totals.allocated}
                            issued={d.totals.issued}
                          />
                        </TableCell>
                        <TableCell align="right">
                          {d.totals.remaining}
                        </TableCell>
                        <TableCell align="right">{activated(d.id)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}
        </>
      )}

      <DistributorDialog
        open={creating}
        distributor={null}
        onClose={() => setCreating(false)}
      />
    </>
  );
}
